import { useRef, useEffect, useState, useCallback, useLayoutEffect, useMemo } from "react";
import type { ReactElement, ReactNode } from "react";
import type {
  ApiFetch,
  DataTableTheme,
  WorkerSchema,
  Transaction,
  WorkspaceConfig,
  DrawerAction,
} from "../types";
import { CellRenderer } from "./CellRenderer";
import { DrawerFieldShell, DrawerReadonlyValue } from "./DrawerFieldShell";
import { EditableProperty } from "./EditableProperty";
import { StatusBadge } from "./StatusBadge";
import { ImageViewer } from "./ImageViewer";
import { DataTableImage } from "./DataTableImage";
import {
  formatAmount,
  formatExpenseDocumentType,
  normalizeExpenseDocumentType,
} from "../utils/formatters";
import { ThaiDatetimeText } from "./ThaiDatetimeText";
import { useWriteQueue } from "../hooks/useWriteQueue";
import { getDisplayText, getDisplayValue } from "../utils/display";
import { resolveTransactionImageCandidates } from "../utils/transactionImages";
import { MissingImagePlaceholder } from "./MissingImagePlaceholder";
import { buildDataTableThemeVars } from "../utils/theme";
import {
  ExternalLink,
  Info,
  CreditCard,
  User,
  Truck,
  ClipboardList,
  StickyNote,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
  FileText,
  History,
  Wallet,
} from "lucide-react";
import { cn } from "../utils/cn";
import { isCancelledTransactionStatus } from "../utils/statusVisuals";
import {
  AeraButton,
  Modal,
  ModalCloseButton,
  ModalHeader,
  ModalShell,
  SegmentedTabSwitcher,
  type SegmentedTabItem,
} from "@aera/ui";
import { TransactionHistoryTab } from "./TransactionHistoryTab";
import { resolveSlipMatchedAmount } from "../utils/incomeDerived";
import {
  fetchTransactionHistory,
  pickLatestTransactionHistoryMeta,
} from "../utils/transactionHistory";

export type RecordDrawerTab = "details" | "allocation" | "history";

export interface RecordDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  record: Transaction | null;
  schema: WorkerSchema;
  workspaceId: string;
  workspaceConfig: WorkspaceConfig;
  apiFetch?: ApiFetch;
  themeOverrides?: Partial<DataTableTheme>;
  onPrev?: () => void;
  onNext?: () => void;
  position?: { current: number; total: number };
  onDrawerAction?: (action: DrawerAction, record: Transaction) => void;
  initialTab?: RecordDrawerTab | null;
  onTabChange?: (tab: RecordDrawerTab) => void;
  /** เรียกหลัง exit animation จบ */
  onExited?: () => void;
  /** Extra content as its own drawer tab (e.g. income money allocation) */
  detailsAddon?:
    | ReactNode
    | ((
        record: Transaction,
        ctx: {
          onAllocationCountChange: (count: number) => void;
          allocationTabActive: boolean;
        },
      ) => ReactNode);
  /** After undo from ประวัติ (income money ops) — refresh table rows */
  onHistoryUndone?: () => void | Promise<void>;
}

const SECTION_ICONS: Record<string, any> = {
  PAYMENT: CreditCard,
  CUSTOMER: User,
  SHIPPING: Truck,
  DETAILS: ClipboardList,
  EXPENSE: CreditCard,
  TAX: StickyNote,
};

const SECTION_LABELS_TH: Record<string, string> = {
  PAYMENT: "การชำระเงิน",
  CUSTOMER: "ลูกค้า",
  SHIPPING: "จัดส่ง",
  DETAILS: "รายละเอียด",
  EXPENSE: "รายจ่าย",
  TAX: "ภาษี",
};

function normalizeCopyValue(value: unknown) {
  return getDisplayText(value)
    .replace(/\s+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function uniqueNonEmpty(items: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const v = (raw ?? "").trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

function isMultilineFieldValue(fieldId: string, value: unknown) {
  const text = getDisplayText(value);
  return (
    text.includes("\n") ||
    text.length > 48 ||
    ["address", "items", "extra_info", "remark", "province"].includes(fieldId)
  );
}

const RECORD_DRAWER_PANEL_SELECTOR =
  "[data-aera-modal-panel].record-drawer-modal-panel";

const RECORD_DRAWER_TAB_DETAILS: SegmentedTabItem<RecordDrawerTab> = {
  id: "details",
  label: "ข้อมูล",
  icon: FileText,
};
const RECORD_DRAWER_TAB_ALLOCATION: SegmentedTabItem<RecordDrawerTab> = {
  id: "allocation",
  label: "การจัดสรร",
  icon: Wallet,
};
const RECORD_DRAWER_TAB_HISTORY: SegmentedTabItem<RecordDrawerTab> = {
  id: "history",
  label: "ประวัติ",
  icon: History,
};

function useMinWidthMd() {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return matches;
}

function RecordDrawerCancelSheet({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer: ReactNode;
}) {
  const isDesktop = useMinWidthMd();
  const [scopedHost, setScopedHost] = useState<Element | null>(null);

  useLayoutEffect(() => {
    if (!isDesktop) {
      setScopedHost(null);
      return;
    }
    if (!isOpen) return;
    setScopedHost(document.querySelector(RECORD_DRAWER_PANEL_SELECTOR));
  }, [isOpen, isDesktop]);

  const isScoped = isDesktop && scopedHost != null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      onExited={() => setScopedHost(null)}
      align="bottom"
      scopedHost={isScoped ? scopedHost : null}
      zIndexClass="z-[10050]"
      panelClassName={[
        "flex flex-col overflow-hidden rounded-t-2xl rounded-b-none md:rounded-b-4xl border-t border-aera-lightgray bg-aera-white shadow-xl",
        isScoped ? "max-h-[85%]" : "max-h-[80dvh]",
      ].join(" ")}
    >
      <div className="flex shrink-0 justify-center pb-1 pt-2">
        <div className="h-1 w-10 rounded-full bg-aera-lightgray" aria-hidden />
      </div>

      <header className="flex shrink-0 items-center justify-between gap-3 px-4 pb-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-aera-charcoal">{title}</h2>
          {subtitle ? (
            <p className="mt-0.5 text-sm text-aera-gray">{subtitle}</p>
          ) : null}
        </div>
        <ModalCloseButton onClose={onClose} className="-mr-1 shrink-0" />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>

      <div className="shrink-0 space-y-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {footer}
      </div>
    </ModalShell>
  );
}

export function RecordDrawer({
  open,
  onOpenChange,
  record,
  schema,
  workspaceId,
  workspaceConfig,
  apiFetch,
  themeOverrides,
  onPrev,
  onNext,
  position,
  onDrawerAction,
  initialTab = null,
  onTabChange,
  onExited,
  detailsAddon,
  onHistoryUndone,
}: RecordDrawerProps): ReactElement | null {
  const { patchWithQueue } = useWriteQueue(
    workspaceId,
    schema.workerId,
    apiFetch,
  );
  const firstEditableRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [imageCandidateIndex, setImageCandidateIndex] = useState(0);
  const [slipLoadFailed, setSlipLoadFailed] = useState(false);
  const [copiedRecordId, setCopiedRecordId] = useState(false);
  const [copiedFieldId, setCopiedFieldId] = useState<string | null>(null);
  const [draftChanges, setDraftChanges] = useState<Record<string, string>>({});
  /** Values saved to the API but not yet reflected on `record` (stale selected row). */
  const [savedOverrides, setSavedOverrides] = useState<Record<string, string>>(
    {},
  );
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [discardPrompt, setDiscardPrompt] = useState<{
    message: string;
    confirmLabel: string;
    onConfirm: () => void;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<RecordDrawerTab>("details");
  const [latestHistoryMeta, setLatestHistoryMeta] = useState<{
    label: string;
    actorName: string;
    occurredAt: string;
  } | null>(null);
  const [historyCount, setHistoryCount] = useState(0);
  const [historyCountLoading, setHistoryCountLoading] = useState(false);
  const [allocationCount, setAllocationCount] = useState(0);
  const [allocationCountLoading, setAllocationCountLoading] = useState(false);

  const historyEnabled = schema.historyEnabled === true;
  const hasAllocationAddon = detailsAddon != null;
  const handleAllocationCountChange = useCallback((count: number) => {
    setAllocationCount(Math.max(0, count));
    setAllocationCountLoading(false);
  }, []);
  const drawerTabs = useMemo(() => {
    const tabs: SegmentedTabItem<RecordDrawerTab>[] = [
      RECORD_DRAWER_TAB_DETAILS,
    ];
    if (hasAllocationAddon) {
      tabs.push({
        ...RECORD_DRAWER_TAB_ALLOCATION,
        count: allocationCount > 0 ? allocationCount : undefined,
        countLoading: allocationCountLoading,
      });
    }
    if (historyEnabled) {
      tabs.push({
        ...RECORD_DRAWER_TAB_HISTORY,
        count: historyCount > 0 ? historyCount : undefined,
        countLoading: historyCountLoading,
      });
    }
    return tabs;
  }, [
    allocationCount,
    allocationCountLoading,
    hasAllocationAddon,
    historyCount,
    historyCountLoading,
    historyEnabled,
  ]);
  const showDrawerTabs = drawerTabs.length > 1;

  const isValidDrawerTab = useCallback(
    (tab: string | null | undefined): tab is RecordDrawerTab => {
      if (tab === "details") return true;
      if (tab === "allocation") return hasAllocationAddon;
      if (tab === "history") return historyEnabled;
      return false;
    },
    [hasAllocationAddon, historyEnabled],
  );

  useEffect(() => {
    if (!copiedRecordId) return;

    const timer = window.setTimeout(() => setCopiedRecordId(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copiedRecordId]);

  useEffect(() => {
    if (!copiedFieldId) return;

    const timer = window.setTimeout(() => setCopiedFieldId(null), 1400);
    return () => window.clearTimeout(timer);
  }, [copiedFieldId]);

  useEffect(() => {
    if (!open) {
      setShowCancelConfirm(false);
      setIsCancelling(false);
      setDiscardPrompt(null);
      setActiveTab("details");
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (isValidDrawerTab(initialTab)) {
      setActiveTab(initialTab);
    }
  }, [initialTab, isValidDrawerTab, open]);

  const amountColId = schema.columns.find((c) => c.type === "amount")?.id;
  const imageColId = schema.columns.find((c) => c.type === "image")?.id;
  const statusColId = schema.columns.find((c) => c.type === "status")?.id;

  const fireToast = (message: string) => {
    try {
      window.dispatchEvent(
        new CustomEvent("aera:toast", { detail: { message } }),
      );
    } catch {
      /* ignore when window unavailable */
    }
  };

  const fireCopyToast = () => fireToast("คัดลอกแล้ว");

  const handleCopyRecordId = async (activeRecord: Transaction) => {
    try {
      await navigator.clipboard.writeText(
        getDisplayText(activeRecord.record_id),
      );
      setCopiedRecordId(true);
      fireCopyToast();
    } catch (error) {
      console.error("Failed to copy record id", error);
    }
  };

  const handleCopyField = async (fieldId: string, rawValue: unknown) => {
    const value = normalizeCopyValue(rawValue);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedFieldId(fieldId);
      fireCopyToast();
    } catch (error) {
      console.error("Failed to copy field value", fieldId, error);
    }
  };

  const handlePatch = async (
    recordId: string,
    field: string,
    value: string,
  ) => {
    await patchWithQueue({ recordId, field, value });
    // Keep showing the saved value until parent `record` catches up
    // (selected row is a snapshot and is not updated by the query cache alone).
    setSavedOverrides((current) => ({ ...current, [field]: value }));
  };

  const editableFields = schema.columns.filter((col) => col.editable);
  const firstEditableFieldId = editableFields[0]?.id;
  const actions = (schema.drawerActions ?? []).filter(
    (action) => !action.scope || action.scope.includes(schema.workerId),
  );

  const [frozenRecord, setFrozenRecord] = useState<Transaction | null>(null);

  useEffect(() => {
    if (open && record) {
      setFrozenRecord(record);
    }
  }, [open, record]);

  const activeRecord = open && record ? record : frozenRecord;

  const recordIdText = activeRecord
    ? getDisplayText(activeRecord.record_id)
    : "";

  useEffect(() => {
    setDraftChanges({});
    setSavedOverrides({});
    setIsSavingDraft(false);
    if (open && isValidDrawerTab(initialTab)) {
      setActiveTab(initialTab);
    } else {
      setActiveTab("details");
    }
    setLatestHistoryMeta(null);
    setHistoryCount(0);
    setHistoryCountLoading(false);
    setAllocationCount(0);
    setAllocationCountLoading(false);
  }, [initialTab, isValidDrawerTab, open, recordIdText]);

  useEffect(() => {
    if (!open || !hasAllocationAddon) {
      setAllocationCount(0);
      setAllocationCountLoading(false);
      return;
    }
    setAllocationCount(0);
    setAllocationCountLoading(true);
  }, [hasAllocationAddon, open, recordIdText]);

  // Drop overrides once the live record shows the same saved value.
  useEffect(() => {
    if (!activeRecord) return;
    setSavedOverrides((current) => {
      if (Object.keys(current).length === 0) return current;
      let next: Record<string, string> | null = null;
      for (const [field, value] of Object.entries(current)) {
        const live = getDisplayText(
          activeRecord[field as keyof Transaction],
        );
        if (live === value) {
          if (!next) next = { ...current };
          delete next[field];
        }
      }
      return next ?? current;
    });
  }, [activeRecord]);

  useEffect(() => {
    if (!open || !historyEnabled || !recordIdText?.trim() || !workspaceId?.trim()) {
      setLatestHistoryMeta(null);
      setHistoryCount(0);
      setHistoryCountLoading(false);
      return;
    }
    let cancelled = false;
    setHistoryCount(0);
    setHistoryCountLoading(true);
    void fetchTransactionHistory(recordIdText, workspaceId)
      .then((entries) => {
        if (cancelled) return;
        setHistoryCount(entries.length);
        setLatestHistoryMeta(pickLatestTransactionHistoryMeta(entries));
      })
      .catch(() => {
        if (cancelled) return;
        setHistoryCount(0);
        setLatestHistoryMeta(null);
      })
      .finally(() => {
        if (!cancelled) setHistoryCountLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [historyEnabled, open, recordIdText, workspaceId]);

  const fieldDisplayValue = useCallback(
    (fieldId: string) => {
      if (!activeRecord) return "";
      if (fieldId in draftChanges) return draftChanges[fieldId];
      if (fieldId in savedOverrides) return savedOverrides[fieldId];
      return getDisplayText(activeRecord[fieldId as keyof Transaction]);
    },
    [activeRecord, draftChanges, savedOverrides],
  );

  const hasDraftChanges = useMemo(() => {
    if (!activeRecord) return false;
    return Object.entries(draftChanges).some(([field, value]) => {
      const baseline =
        field in savedOverrides
          ? savedOverrides[field]
          : getDisplayText(activeRecord[field as keyof Transaction]);
      return value !== baseline;
    });
  }, [activeRecord, draftChanges, savedOverrides]);

  const handleDraftChange = useCallback(
    (fieldId: string, value: string) => {
      if (!activeRecord) return;
      const baseline =
        fieldId in savedOverrides
          ? savedOverrides[fieldId]
          : getDisplayText(activeRecord[fieldId as keyof Transaction]);
      setDraftChanges((current) => {
        if (value === baseline) {
          if (!(fieldId in current)) return current;
          const next = { ...current };
          delete next[fieldId];
          return next;
        }
        return { ...current, [fieldId]: value };
      });
    },
    [activeRecord, savedOverrides],
  );

  const handleSaveDraft = useCallback(async () => {
    if (!activeRecord || !hasDraftChanges) return;

    const pending = Object.entries(draftChanges).filter(([field, value]) => {
      const baseline =
        field in savedOverrides
          ? savedOverrides[field]
          : getDisplayText(activeRecord[field as keyof Transaction]);
      return value !== baseline;
    });
    if (pending.length === 0) return;

    setIsSavingDraft(true);
    try {
      for (const [field, value] of pending) {
        await handlePatch(recordIdText, field, value);
      }
      setDraftChanges({});
      fireToast("บันทึกแล้ว");
    } catch (error) {
      console.error("Failed to save drawer draft", error);
      fireToast("บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง");
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    activeRecord,
    draftChanges,
    handlePatch,
    hasDraftChanges,
    recordIdText,
    savedOverrides,
  ]);

  const handleRequestClose = useCallback(() => {
    if (hasDraftChanges) {
      setDiscardPrompt({
        message:
          "มีการแก้ไขที่ยังไม่ได้บันทึก ต้องการปิดโดยไม่บันทึกหรือไม่?",
        confirmLabel: "ปิดโดยไม่บันทึก",
        onConfirm: () => {
          setDraftChanges({});
          setDiscardPrompt(null);
          onOpenChange(false);
        },
      });
      return;
    }
    onOpenChange(false);
  }, [hasDraftChanges, onOpenChange]);

  const handleRequestNavigate = useCallback(
    (navigate: () => void) => {
      if (hasDraftChanges) {
        setDiscardPrompt({
          message:
            "มีการแก้ไขที่ยังไม่ได้บันทึก ต้องการเปลี่ยนรายการโดยไม่บันทึกหรือไม่?",
          confirmLabel: "เปลี่ยนโดยไม่บันทึก",
          onConfirm: () => {
            setDraftChanges({});
            setDiscardPrompt(null);
            navigate();
          },
        });
        return;
      }
      navigate();
    },
    [hasDraftChanges],
  );

  useEffect(() => {
    if (!open) return;

    const handleKey = (e: KeyboardEvent) => {
      if (discardPrompt || showCancelConfirm) return;
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handleRequestNavigate(() => onPrev?.());
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        handleRequestNavigate(() => onNext?.());
      }
      if (e.key === "Escape") {
        e.preventDefault();
        handleRequestClose();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [
    discardPrompt,
    handleRequestClose,
    handleRequestNavigate,
    onNext,
    onPrev,
    open,
    showCancelConfirm,
  ]);

  const handleModalExited = useCallback(() => {
    setFrozenRecord(null);
    setDraftChanges({});
    setIsSavingDraft(false);
    onExited?.();
  }, [onExited]);

  const imageCandidates = useMemo(() => {
    if (!activeRecord) return [];
    const columnValue =
      activeRecord.image_url ??
      (imageColId ? activeRecord[imageColId as keyof Transaction] : null);
    return resolveTransactionImageCandidates({
      record: activeRecord as Record<string, unknown>,
      workspaceId,
      columnValue,
    });
  }, [activeRecord, imageColId, workspaceId]);
  const imageCandidateKey = imageCandidates.join("\0");

  useEffect(() => {
    setImageCandidateIndex(0);
    setSlipLoadFailed(false);
  }, [activeRecord?.record_id, imageCandidateKey]);

  const imageUrl = imageCandidates[imageCandidateIndex] ?? null;

  if (!activeRecord) return null;
  if (typeof document === "undefined") return null;

  const amount = activeRecord && amountColId ? activeRecord[amountColId] : null;
  const status =
    activeRecord && statusColId
      ? getDisplayText(activeRecord[statusColId])
      : null;

  const amountValue = getDisplayValue(amount);
  const recordedAtValue = getDisplayValue(activeRecord.recorded_at);
  const isIncomeWorker = schema.workerId === "bill-lin-income";
  const jobTypeText = isIncomeWorker
    ? getDisplayText(activeRecord.job_type)
    : "";
  const slipMatchedAmount = isIncomeWorker
    ? resolveSlipMatchedAmount(activeRecord)
    : null;
  const isMatchedIncomeCancel =
    isIncomeWorker &&
    (jobTypeText.includes("จับคู่") ||
      (slipMatchedAmount != null && slipMatchedAmount > 0) ||
      (status != null && status.includes("จับคู่")));
  const visibleActions = actions.filter(
    (action) =>
      action.id !== "cancel-record" || !isCancelledTransactionStatus(status),
  );
  const headerActionIds = new Set(["download-summary", "cancel-record"]);
  const headerActions = visibleActions.filter((action) =>
    headerActionIds.has(action.id),
  );
  const footerActions = visibleActions.filter(
    (action) => !headerActionIds.has(action.id),
  );

  const actionFooter = (
    <>
      <AeraButton
        type="button"
        variant="secondary"
        fontWeight="medium"
        disabled={!hasDraftChanges || isSavingDraft}
        onClick={() => void handleSaveDraft()}
      >
        {isSavingDraft ? "กำลังบันทึก…" : "บันทึก"}
      </AeraButton>
      {footerActions.map((action) => (
        <ActionButton
          key={action.id}
          action={action}
          onCancelClick={() => setShowCancelConfirm(true)}
          onAction={(nextAction) => onDrawerAction?.(nextAction, activeRecord)}
        />
      ))}
    </>
  );

  const drawerNav = (
    <div className="flex w-full items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <TopNavButton
          icon={ChevronLeft}
          label="ก่อนหน้า"
          onClick={() => handleRequestNavigate(() => onPrev?.())}
          disabled={!onPrev}
        />
        <TopNavButton
          icon={ChevronRight}
          label="ถัดไป"
          onClick={() => handleRequestNavigate(() => onNext?.())}
          disabled={!onNext}
        />
        {position && (
          <div className="ml-1 px-1 py-1 text-[11px] font-bold tabular-nums tracking-wide text-aera-gray">
            {position.current} / {position.total}
          </div>
        )}
      </div>

      <ModalCloseButton onClose={handleRequestClose} className="-mr-1" />
    </div>
  );

  const drawerSummary = (
    <div className="border-b border-aera-lightgray/80 pb-4 mb-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-3xl font-bold text-aera-charcoal tracking-tight">
              {formatAmount(amountValue as number | null | undefined)}
            </h2>
            {status && (
              <StatusBadge
                status={status}
                className="px-3 py-1 text-xs font-bold shadow-none"
              />
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-aera-lightslate">
              <AeraButton
                variant="ghost"
                size="xs"
                type="button"
                onClick={() => void handleCopyRecordId(activeRecord)}
                className="appearance-none bg-transparent! border-0 p-0! text-[11px]! text-aera-gray tracking-tight transition-colors hover:text-aera-charcoal hover:bg-transparent!"
              >
                {copiedRecordId ? "คัดลอกแล้ว" : recordIdText}
              </AeraButton>
              <span className="text-aera-lightslate/60">•</span>
              <ThaiDatetimeText
                variant="meta"
                value={recordedAtValue as string | null | undefined}
              />
            </div>
            {historyEnabled && latestHistoryMeta && activeTab === "details" ? (
              <button
                type="button"
                onClick={() => setActiveTab("history")}
                className="cursor-pointer text-left text-xs font-medium text-aera-gray transition-colors hover:text-aera-charcoal"
              >
                ล่าสุด:{" "}
                <span className="font-semibold text-aera-charcoal">
                  {latestHistoryMeta.label}
                </span>
                <span className="mx-1.5 text-aera-lightslate/70">·</span>
                <span className="tabular-nums">{latestHistoryMeta.occurredAt}</span>
                <span className="ml-1 text-aera-amber">ดูประวัติ →</span>
              </button>
            ) : null}
          </div>
        </div>
        {headerActions.length > 0 ? (
          <div className="flex shrink-0 flex-row sm:flex-col items-center gap-2 justify-end">
            {headerActions.map((action) => (
              <ActionButton
                key={action.id}
                action={action}
                size="xs"
                onCancelClick={() => setShowCancelConfirm(true)}
                onAction={(nextAction) =>
                  onDrawerAction?.(nextAction, activeRecord)
                }
              />
            ))}
          </div>
        ) : null}
      </div>

      {showDrawerTabs ? (
        <div className="mt-4 border-t border-aera-lightgray/80 pt-3 [&_button]:cursor-pointer [&_button:disabled]:cursor-not-allowed">
          <SegmentedTabSwitcher
            tabs={drawerTabs}
            activeTab={activeTab}
            onChange={(next) => {
              setActiveTab(next);
              onTabChange?.(next);
            }}
            ariaLabel="เมนูรายการธุรกรรม"
          />
        </div>
      ) : null}
    </div>
  );

  const drawerDetails = (
    <div className="space-y-4">
      {imageUrl || imageCandidates.length > 0 ? (
        <>
          <div
            role={slipLoadFailed ? undefined : "button"}
            onClick={slipLoadFailed ? undefined : () => setViewerOpen(true)}
            className={`group relative flex h-24 w-full items-center gap-4 overflow-hidden rounded-lg bg-aera-white p-4 transition-all ${
              slipLoadFailed
                ? "cursor-default"
                : "cursor-pointer hover:bg-aera-lightgray/50 active:scale-[0.99]"
            }`}
          >
            <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-md bg-(--aera-dt-surface-muted)">
              {slipLoadFailed || !imageUrl ? (
                <MissingImagePlaceholder iconClassName="h-5 w-5" />
              ) : (
                <DataTableImage
                  src={imageUrl}
                  alt="Receipt"
                  fill
                  sizes="56px"
                  className="object-cover transition-transform group-hover:scale-110"
                  onError={() => {
                    if (imageCandidateIndex + 1 < imageCandidates.length) {
                      setImageCandidateIndex((index) => index + 1);
                      return;
                    }
                    setSlipLoadFailed(true);
                  }}
                />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-aera-gray">
                หลักฐานการทำรายการ
              </p>
              <p className="mt-0.5 text-xs font-medium text-aera-lightslate">
                {slipLoadFailed || !imageUrl
                  ? "ไม่พบรูปภาพ"
                  : "แตะเพื่อดูภาพขนาดเต็มและขยาย"}
              </p>
            </div>
            {!slipLoadFailed && imageUrl ? (
              <ExternalLink className="h-5 w-5 text-aera-lightslate transition-colors group-hover:text-aera-gray" />
            ) : null}
          </div>
          {!slipLoadFailed && imageUrl ? (
            <ImageViewer
              open={viewerOpen}
              onOpenChange={setViewerOpen}
              imageUrl={imageUrl}
              caption={`${recordIdText} · ${formatAmount(amountValue as number | null | undefined)}`}
              themeOverrides={themeOverrides}
            />
          ) : null}
        </>
      ) : null}

      {schema.drawerSections.map((section) => {
        if (
          section.configGate &&
          !section.configGate.includes(workspaceConfig.bill_lin_config)
        )
          return null;
        const SectionIcon = SECTION_ICONS[section.label] || Info;
        const sectionTitle = SECTION_LABELS_TH[section.label] ?? section.label;

        return (
          <div
            key={section.label}
            className="animate-in fade-in slide-in-from-top-2 duration-500 fill-mode-both"
          >
            <div className="rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated) overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-(--aera-dt-border-soft) bg-aera-white">
                <div className="w-7 h-7 rounded-lg bg-(--aera-dt-row-selected-surface) flex items-center justify-center text-(--aera-dt-accent-hover)">
                  <SectionIcon className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-bold text-aera-charcoal tracking-tight">
                  {sectionTitle}
                </h3>
              </div>
              <div className="divide-y divide-(--aera-dt-border-soft)">
                {section.fields.map((fieldId) => {
                  const col = schema.columns.find((c) => c.id === fieldId);
                  if (!col) return null;
                  const rawValue =
                    fieldId === "slip_matched_amount"
                      ? resolveSlipMatchedAmount(activeRecord)
                      : activeRecord[fieldId];
                  if (
                    fieldId === "slip_matched_amount" &&
                    (rawValue == null || rawValue === "")
                  ) {
                    return null;
                  }
                  const displayValue = getDisplayText(rawValue);
                  const editableValue = fieldDisplayValue(fieldId);
                  const hasValue = displayValue.trim() !== "";
                  const isCopyable =
                    !col.editable && hasValue && col.copyable === true;
                  const isMultiline = isMultilineFieldValue(fieldId, rawValue);

                  return (
                    <div key={fieldId} className="px-4 py-3 space-y-1.5">
                      <label className="block text-xs font-semibold leading-5 text-aera-lightslate">
                        {col.header}
                      </label>
                      <div className="min-w-0">
                        {col.editable ? (
                          <DrawerFieldShell multiline={isMultiline} editable>
                            {col.id === "document_type" ? (
                              <div className="relative flex w-full items-center justify-between gap-3">
                                <span className="font-medium leading-5 text-aera-gray">
                                  {formatExpenseDocumentType(editableValue) ||
                                    "—"}
                                </span>
                                <select
                                  aria-label={col.header}
                                  value={
                                    normalizeExpenseDocumentType(
                                      editableValue,
                                    ) || "receipt"
                                  }
                                  onChange={(event) =>
                                    handleDraftChange(
                                      fieldId,
                                      event.target.value,
                                    )
                                  }
                                  className={cn(
                                    "absolute inset-0 h-full w-full opacity-0",
                                    "cursor-pointer",
                                  )}
                                >
                                  <option value="receipt">ใบเสร็จ</option>
                                  <option value="invoice">ใบแจ้งหนี้</option>
                                </select>
                              </div>
                            ) : col.type === "status" ? (
                              <div className="relative flex w-full items-center justify-between gap-3">
                                <StatusBadge
                                  status={editableValue}
                                  className="px-3 py-1 text-xs font-bold shadow-none"
                                />
                                <select
                                  aria-label={col.header}
                                  value={editableValue}
                                  onChange={(event) =>
                                    handleDraftChange(fieldId, event.target.value)
                                  }
                                  className={cn(
                                    "absolute inset-0 h-full w-full opacity-0",
                                    "cursor-pointer",
                                  )}
                                >
                                  {uniqueNonEmpty([
                                    ...(schema.filterOptions?.status ?? []),
                                    editableValue,
                                  ]).map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ) : (
                              <EditableProperty
                                value={editableValue}
                                recordId={recordIdText}
                                field={fieldId}
                                inputRef={
                                  fieldId === firstEditableFieldId
                                    ? firstEditableRef
                                    : undefined
                                }
                                onSave={handlePatch}
                                onDraftChange={(value) =>
                                  handleDraftChange(fieldId, value)
                                }
                                autoSave={false}
                                multiline={isMultiline}
                              />
                            )}
                          </DrawerFieldShell>
                        ) : isCopyable ? (
                          <AeraButton
                            variant="ghost"
                            size="xs"
                            type="button"
                            onClick={() =>
                              void handleCopyField(fieldId, rawValue)
                            }
                            className={cn(
                              "group w-full appearance-none bg-transparent! border-0! shadow-none p-0! cursor-pointer text-left hover:bg-transparent! transition-colors",
                              isMultiline
                                ? "relative pr-20"
                                : "flex items-center justify-between gap-3",
                            )}
                          >
                            <DrawerReadonlyValue
                              multiline={isMultiline}
                              className={cn(!isMultiline && "flex-1")}
                            >
                              <CellRenderer
                                col={col}
                                value={rawValue}
                                record={activeRecord}
                                themeOverrides={themeOverrides}
                                workspaceId={workspaceId}
                                variant="drawer"
                              />
                            </DrawerReadonlyValue>
                            <div
                              className={cn(
                                "inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-aera-lightslate transition-colors group-hover:text-aera-gray",
                                isMultiline
                                  ? "absolute right-0 top-0 pt-1"
                                  : "h-5",
                              )}
                            >
                              {copiedFieldId === fieldId ? (
                                <>
                                  <Check className="h-3.5 w-3.5 text-(--aera-dt-success-text)" />
                                  <span className="text-(--aera-dt-success-text)">
                                    คัดลอกแล้ว
                                  </span>
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3.5 w-3.5" />
                                  <span>คัดลอก</span>
                                </>
                              )}
                            </div>
                          </AeraButton>
                        ) : (
                          <DrawerReadonlyValue multiline={isMultiline}>
                            <CellRenderer
                              col={col}
                              value={rawValue}
                              record={activeRecord}
                              themeOverrides={themeOverrides}
                              workspaceId={workspaceId}
                              variant="drawer"
                            />
                          </DrawerReadonlyValue>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const drawerAllocation = (
    <>
      {activeRecord
        ? typeof detailsAddon === "function"
          ? detailsAddon(activeRecord, {
              onAllocationCountChange: handleAllocationCountChange,
              allocationTabActive: activeTab === "allocation",
            })
          : detailsAddon
        : null}
    </>
  );

  const cancelConfirmSheet = (
    <RecordDrawerCancelSheet
      isOpen={showCancelConfirm}
      onClose={() => {
        if (isCancelling) return;
        setShowCancelConfirm(false);
      }}
      title={
        <span className="flex items-center gap-2 text-(--aera-dt-danger-text)">
          <AlertTriangle className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />
          ยืนยันการยกเลิกรายการ
        </span>
      }
      subtitle={
        <>
          {recordIdText}{" "}
          <span className="text-aera-gray">•</span>{" "}
          {formatAmount(amountValue as number | null | undefined)}
        </>
      }
      footer={
        <>
          <AeraButton
            type="button"
            variant="danger"
            fontWeight="medium"
            fullWidth
            className="cursor-pointer"
            loading={isCancelling}
            disabled={isCancelling}
            onClick={async () => {
              if (isCancelling) return;
              const statusField = statusColId ?? "status";
              setIsCancelling(true);
              try {
                await handlePatch(recordIdText, statusField, "❌ ยกเลิก");
                setShowCancelConfirm(false);
                onOpenChange(false);
                // Sticky "ธุรกรรมล่าสุด / ย้อนกลับ" is for the match op — cancel already
                // reversed money, so drop the bar (same as after a successful undo).
                if (typeof window !== "undefined") {
                  window.dispatchEvent(
                    new CustomEvent("aera:clear-latest-money-undo", {
                      detail: {
                        reason: "record_cancelled",
                        recordId: recordIdText,
                      },
                    }),
                  );
                }
              } catch (error) {
                console.error("Failed to cancel record", error);
                fireToast(
                  error instanceof Error && error.message.trim()
                    ? error.message
                    : "ยกเลิกรายการไม่สำเร็จ ลองใหม่อีกครั้ง",
                );
              } finally {
                setIsCancelling(false);
              }
            }}
          >
            {isCancelling ? "กำลังยกเลิก…" : "ยืนยันยกเลิก"}
          </AeraButton>
          <AeraButton
            type="button"
            variant="outline"
            fontWeight="medium"
            fullWidth
            className="cursor-pointer"
            disabled={isCancelling}
            onClick={() => setShowCancelConfirm(false)}
          >
            ปิด
          </AeraButton>
        </>
      }
    >
      <div
        className="text-aera-charcoal"
        style={buildDataTableThemeVars(themeOverrides)}
      >
        <p className="text-sm leading-relaxed text-aera-gray">
          การยกเลิกจะเปลี่ยนสถานะเป็น{" "}
          <span className="font-bold text-(--aera-dt-danger-text)">
            ❌ ยกเลิก
          </span>{" "}
          และไม่สามารถย้อนกลับได้
        </p>
        {isMatchedIncomeCancel ? (
          <p className="mt-3 text-sm leading-relaxed text-aera-gray">
            รายการนี้เคยจับคู่ชำระเงินแล้ว — การยกเลิกจะสั่ง{" "}
            <span className="font-semibold text-aera-charcoal">
              ยกเลิกการจับคู่บนฝั่งเงินจริง
            </span>{" "}
            พร้อมเปลี่ยนสถานะรายรับ หากยกเลิกจับคู่ไม่สำเร็จ
            ระบบจะไม่เปลี่ยนสถานะรายการ
          </p>
        ) : null}
      </div>
    </RecordDrawerCancelSheet>
  );

  const discardConfirmSheet = (
    <RecordDrawerCancelSheet
      isOpen={discardPrompt != null}
      onClose={() => setDiscardPrompt(null)}
      title={
        <span className="flex items-center gap-2 text-aera-amberdark">
          <AlertTriangle className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />
          ทิ้งการแก้ไข?
        </span>
      }
      footer={
        <>
          <AeraButton
            type="button"
            variant="danger"
            fontWeight="medium"
            fullWidth
            className="cursor-pointer"
            onClick={() => discardPrompt?.onConfirm()}
          >
            {discardPrompt?.confirmLabel ?? "ทิ้งการแก้ไข"}
          </AeraButton>
          <AeraButton
            type="button"
            variant="outline"
            fontWeight="medium"
            fullWidth
            className="cursor-pointer"
            onClick={() => setDiscardPrompt(null)}
          >
            ยกเลิก
          </AeraButton>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-aera-gray">
        {discardPrompt?.message}
      </p>
    </RecordDrawerCancelSheet>
  );

  const themedContent = (
    <div
      className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden text-aera-charcoal"
      style={buildDataTableThemeVars(themeOverrides)}
    >
      <div className="shrink-0">{drawerSummary}</div>
      <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
        <div
          key={activeTab === "allocation" ? "allocation" : activeTab}
          className={`animate-in fade-in duration-300 fill-mode-both ${
            activeTab === "history" || activeTab === "allocation"
              ? "slide-in-from-right-2"
              : "slide-in-from-left-2"
          }`}
        >
          {historyEnabled && activeTab === "history" ? (
            <TransactionHistoryTab
              record={activeRecord}
              schema={schema}
              workspaceId={workspaceId}
              onUndone={onHistoryUndone}
            />
          ) : activeTab === "details" || !hasAllocationAddon ? (
            drawerDetails
          ) : null}
        </div>
        {/* Keep mounted so tab badge can load count before the user opens การจัดสรร. */}
        {hasAllocationAddon ? (
          <div
            className={
              activeTab === "allocation"
                ? "animate-in fade-in slide-in-from-right-2 duration-300 fill-mode-both"
                : "hidden"
            }
            aria-hidden={activeTab !== "allocation"}
          >
            {drawerAllocation}
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={open}
      onClose={handleRequestClose}
      onExited={handleModalExited}
      header={<ModalHeader>{drawerNav}</ModalHeader>}
      footer={actionFooter}
      size="2xl"
      placement="responsive-drawer"
      sheetHost
      panelClassName="record-drawer-modal-panel"
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0 [&>div]:flex [&>div]:h-full [&>div]:min-h-0 [&>div]:flex-1 [&>div]:flex-col"
      zIndexClass="z-9998"
    >
      {themedContent}
      {discardConfirmSheet}
      {cancelConfirmSheet}
    </Modal>
  );
}

function TopNavButton({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: any;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <AeraButton
      variant="ghost"
      size="xs"
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "h-10! w-10! p-0! items-center justify-center rounded-lg transition-all",
        disabled
          ? "cursor-not-allowed bg-transparent! text-aera-lightslate"
          : "bg-transparent! text-aera-gray hover:bg-aera-lightgray/50! hover:text-aera-charcoal active:scale-[0.98]",
      )}
    >
      <Icon className="h-4 w-4" />
    </AeraButton>
  );
}

function ActionButton({
  action,
  onCancelClick,
  onAction,
  size = "sm",
}: {
  action: DrawerAction;
  onCancelClick: () => void;
  onAction?: (action: DrawerAction) => void;
  size?: "xs" | "sm";
}) {
  if (action.id === "download-summary") {
    const downloadDisabled = !onAction;
    return (
      <AeraButton
        type="button"
        variant="primary"
        size={size}
        fontWeight="medium"
        onClick={() => onAction?.(action)}
        disabled={downloadDisabled}
        title={downloadDisabled ? "เร็วๆ นี้" : undefined}
      >
        {action.label}
      </AeraButton>
    );
  }

  if (action.id === "cancel-record") {
    return (
      <AeraButton
        type="button"
        variant="danger"
        size={size}
        fontWeight="medium"
        onClick={onCancelClick}
        className="rounded-xl"
      >
        {action.label}
      </AeraButton>
    );
  }

  return (
    <AeraButton
      type="button"
      variant="ghost"
      size={size}
      fontWeight="medium"
    >
      {action.label}
    </AeraButton>
  );
}
